import { useCallback, useState } from 'react'

import { Input } from 'antd'
import { faCity, faEnvelope, faGlobe, faIdCard, faLocationArrow, faSave, faStethoscope, faXmark } from '@fortawesome/pro-solid-svg-icons'
import { useSelector } from 'react-redux'
import { isFunction } from 'lodash'

import FormItem from '../../../Components/shared/Forms/FormItem'
import CountriesSelector from '../../../Components/shared/Selectors/CountriesSelector'
import { Properties } from '../../../utils/entities'
import { getErrors, getFormError, getIdOrNull, handleStateSelectId } from '../../../utils'
import IconButton from '../../../Components/antd/Buttons/IconButton'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { Institution, INSTITUTION_VALIDATORS } from '../../../utils/entities/institution'
import { isValid } from '../../../utils/validators'

const { ADDRESS, CITY, COUNTRY, NAME, SECTOR, ZIPCODE } = Properties
/** A student only describes the place he is going to : the acronym, the email and the service are the hospital's own. */
const VALIDATORS = INSTITUTION_VALIDATORS([ADDRESS, CITY, COUNTRY, NAME, ZIPCODE])

const StudentInstitutionForm = ({ data, displayTitle = true, withSector = false, onCancel, onSubmit }) => {
  const t = useSelector(selectTranslate)

  const [formData, setFormData] = useState({ ...data })
  const [error, setError] = useState({ missingFields: [] })

  const handleSubmit = useCallback(() => {
    const institution = new Institution(formData)

    const errors = isValid(institution, VALIDATORS)

    if (errors.keys.length === 0) {
      onSubmit(withSector ? Object.assign(institution, { [SECTOR]: formData[SECTOR] }) : institution)
    } else {
      const details = {}

      Object.keys(errors.messages).forEach(key => {
        details[t(key)] = errors.messages[key].map(m => t(m))
      })

      setError(error => ({
        ...error,
        title: t('form.errors'),
        details,
        visible: true,
        missingFields: errors.keys
      }))
    }
  }, [t, formData, withSector, onSubmit])

  const errors = getErrors(error)

  return (
    <>
      <div className='h-100 w-100 flex-column'>
        {displayTitle && <h3 className='pdl-12'> {t('common.institution')}</h3>}
        <div className='pd-12'>
          <FormItem icon={faIdCard} label='common.name.dd' required>
            <Input
              className={getFormError(errors[NAME])}
              value={formData[NAME]}
              onChange={e => setFormData({ ...formData, [NAME]: e.target.value })}
            />
          </FormItem>
          <FormItem icon={faLocationArrow} label='common.address.dd' required>
            <Input
              className={getFormError(errors[ADDRESS])}
              value={formData.address ?? null}
              onChange={e => setFormData({ ...formData, [ADDRESS]: e.target.value })}
            />
          </FormItem>
          <FormItem icon={faCity} label='common.city.dd' required>
            <Input
              className={getFormError(errors[CITY])}
              value={formData.city ?? null}
              onChange={e => setFormData({ ...formData, [CITY]: e.target.value })}
            />
          </FormItem>
          <FormItem icon={faEnvelope} label='common.zipcode.dd' required>
            <Input
              className={getFormError(errors[ZIPCODE])}
              value={formData.zipcode ?? null}
              onChange={e => setFormData({ ...formData, [ZIPCODE]: e.target.value })}
            />
          </FormItem>
          <FormItem icon={faGlobe} label='common.country.dd' className={`${getFormError(errors[COUNTRY])} form-select`} required>
            <CountriesSelector
              className={getFormError(errors[COUNTRY])}
              values={getIdOrNull(formData[COUNTRY])}
              onChange={handleStateSelectId(setFormData, COUNTRY)}
            />
          </FormItem>
          {withSector && (
            <FormItem icon={faStethoscope} label='common.sector'>
              <Input
                value={formData[SECTOR]?.name ?? null}
                onChange={e => setFormData({ ...formData, [SECTOR]: { ...formData[SECTOR], name: e.target.value } })}
              />
            </FormItem>
          )}
        </div>
      </div>
      <div className='flex-row w-100 j-ctn-e'>
        {isFunction(onCancel) && <IconButton className='mr-8' icon={faXmark} onClick={onCancel} text={t('actions.cancel')} />}
        <IconButton type='primary' icon={faSave} onClick={handleSubmit} text={t('actions.save')} />
      </div>
    </>
  )
}

export default StudentInstitutionForm
